import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import { ExamSessionFilters } from './exam-session-filters.component';
import { ExamSessionList } from './exam-session-list.component';
import { useStyles } from './exam-session-selection.style';
import { ExamSessionOption, useExamSessionSelection } from './use-exam-session-selection.hook';
import Loading from 'src/app/modules/Recruitment/components/loading/loading';
import Empty from 'src/app/modules/Recruitment/components/empty/empty';
import { useTranslation } from '@pruforce/common-adminweb-sdk';

interface ExamSessionSelectionDialogProps {
  open: boolean;
  examType: string;
  examCode: string;
  examCentre: string;
  examineeId: string;
  selectedSession: ExamSessionOption | null;
  onClose: () => void;
  onSelect: (session: ExamSessionOption) => void;
}

export const ExamSessionSelectionDialog = ({
  open,
  examType,
  examCode,
  examCentre,
  examineeId,
  selectedSession,
  onClose,
  onSelect,
}: ExamSessionSelectionDialogProps) => {
  const t = useTranslation();
  const { classes } = useStyles();
  const {
    examSessionOptions,
    yearOptions,
    monthOptions,
    selectedYear,
    selectedMonth,
    pendingSession,
    loading,
    setPendingSession,
    handleYearChange,
    handleMonthChange,
  } = useExamSessionSelection({
    open,
    examType,
    examCode,
    examCentre,
    examineeId,
    selectedSession,
  });

  const handleConfirm = () => {
    if (pendingSession) {
      onSelect(pendingSession);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <Box className={classes.dialogTitleContainer}>
          <Typography className={classes.dialogTitleText} variant="h6" component="span">
            {t('recruitment.exam.registration.adminRegistration.registrationInformation.examDateAndTime')}
          </Typography>
          <IconButton className={classes.closeButton} onClick={onClose} aria-label="close dialog" size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers className={classes.dialogContent}>
        <ExamSessionFilters
          sessionCount={examSessionOptions.length}
          yearOptions={yearOptions}
          monthOptions={monthOptions}
          selectedYear={selectedYear}
          selectedMonth={selectedMonth}
          loading={loading}
          onYearChange={handleYearChange}
          onMonthChange={handleMonthChange}
        />
        {loading ? (
          <Loading />
        ) : examSessionOptions.length === 0 ? (
          <Empty />
        ) : (
          <ExamSessionList
            options={examSessionOptions}
            value={pendingSession?.value || ''}
            onChange={setPendingSession}
          />
        )}

      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Typography variant="body2" className={classes.actionHint}>
          {t('recruitment.exam.registration.adminRegistration.sessionSelection.scrollHint')}
        </Typography>
        <Box className={classes.actionButtons}>
          <Button className={classes.actionButton} variant="outlined" onClick={onClose}>
            {t('app.button.cancel')}
          </Button>
          <Button
            className={classes.actionButton}
            variant="contained"
            color="secondary"
            disabled={!pendingSession || loading}
            onClick={handleConfirm}
          >
            {t('recruitment.exam.registration.adminRegistration.sessionSelection.select')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
